import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './LandingPage.css';
import HeroSection from './HeroSection';
import HowItWorks from './HowItWorks';
import Footer from './Footer';

const LandingPage = ({ setCurrentPage }) => {
  const [email, setEmail] = useState('');

  const handleCTA = () => {
    // Scroll to features or redirect to signup
    document.getElementById('features').scrollIntoView({ behavior: 'smooth' });
  };

  const handleSkillNavigation = (page) => {
    if (setCurrentPage) {
      setCurrentPage(page);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    // Handle email signup
    alert(`Thanks for joining! We'll notify ${email} when we launch.`);
    setEmail('');
  };

  return (
    <div className="landing-page">
      <HeroSection setCurrentPage={setCurrentPage} />
      
      {/* Search Section */}
      <section className="search-section">
        <div className="container">
          <div className="search-content">
            <motion.div 
              className="search-box-main"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
            >
              <i className="fas fa-search search-icon-main"></i>
              <input
                type="text"
                className="search-input-main"
                placeholder="Search technologies... (e.g., React, Python, Docker)"
                onClick={() => setCurrentPage('techtree')}
                readOnly
              />
            </motion.div>
            
            {/* Category Buttons */}
            <motion.div 
              className="category-buttons-main"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
            >
              <div className="category-scroll-main">
                <button 
                  className="category-btn-main active"
                  onClick={() => setCurrentPage('techtree')}
                >
                  <i className="fas fa-th"></i>
                  <span>All Technologies</span>
                </button>
                <button 
                  className="category-btn-main"
                  onClick={() => setCurrentPage('techtree')}
                >
                  <i className="fab fa-react"></i>
                  <span>Frontend</span>
                </button>
                <button 
                  className="category-btn-main"
                  onClick={() => setCurrentPage('techtree')}
                >
                  <i className="fas fa-server"></i>
                  <span>Backend</span>
                </button>
                <button 
                  className="category-btn-main"
                  onClick={() => setCurrentPage('techtree')}
                >
                  <i className="fas fa-database"></i>
                  <span>Database</span>
                </button>
                <button 
                  className="category-btn-main"
                  onClick={() => setCurrentPage('techtree')}
                >
                  <i className="fas fa-tools"></i>
                  <span>DevOps</span>
                </button>
                <button 
                  className="category-btn-main"
                  onClick={() => setCurrentPage('techtree')}
                >
                  <i className="fab fa-aws"></i>
                  <span>AWS</span>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      <HowItWorks />

      {/* Problem Section */}
      <section className="problem-section">
        <div className="section-glow" aria-hidden="true"></div>
        <div className="container">
          <motion.div 
            className="problem-content"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true }}
          >
            <h2>Tired of Getting Lost in the Tech Learning Maze?</h2>
            <div className="problems-grid">
              <div className="problem-item">
                <i className="fas fa-question-circle"></i>
                <h3>Don't know what to learn next</h3>
                <p>Endless tutorials but no clear path forward</p>
              </div>
              <div className="problem-item">
                <i className="fas fa-clock"></i>
                <h3>Wasting time on outdated tech</h3>
                <p>Learning technologies that are already obsolete</p>
              </div>
              <div className="problem-item">
                <i className="fas fa-puzzle-piece"></i>
                <h3>Missing the big picture</h3>
                <p>Understanding individual tools but not how they connect</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="solution-section">
        <div className="section-glow" aria-hidden="true"></div>
        <div className="container">
          <motion.div 
            className="solution-content"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true }}
          >
            <h2>FullstackSchool Shows You the Exact Learning Path</h2>
            <p className="solution-description">
              Interactive tech tree that reveals dependencies, shows real-world connections, 
              and guides you through the optimal learning sequence for any stack.
            </p>
            
            <div className="how-it-works">
              <h3>How It Works</h3>
              <div className="steps-grid">
                <div className="step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>Choose Your Goal</h4>
                    <p>Select frontend, backend, fullstack, or specialized paths</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>Follow the Visual Path</h4>
                    <p>See exactly which technologies lead to others</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>Learn & Practice</h4>
                    <p>Interactive cards with resources and real examples</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Skills Overview Section */}
      <section id="skills" className="skills-section">
        <div className="container">
          <div className="skills-header">
            <h2>Explore Technologies by Category</h2>
            <p>Choose your learning path and discover the technologies that power modern applications</p>
          </div>
          
          <div className="skills-grid">
            <motion.div 
              className="skill-category-card"
              whileHover={{ 
                scale: 1.03, 
                y: -8,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
              whileTap={{ scale: 0.97 }}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.02, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
              onClick={() => handleSkillNavigation('techtree')}
            >
              <div className="skill-icon">
                <i className="fab fa-react"></i>
              </div>
              <h3>Frontend Development</h3>
              <p>React, Vue, Angular, and modern UI/UX technologies</p>
              <div className="skill-tech-list">
                <span>React</span>
                <span>Vue.js</span>
                <span>Angular</span>
                <span>TypeScript</span>
              </div>
              <button className="skill-explore-btn">Explore Tech Tree</button>
            </motion.div>

            <motion.div 
              className="skill-category-card"
              whileHover={{ 
                scale: 1.03, 
                y: -8,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
              whileTap={{ scale: 0.97 }}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.04, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
              onClick={() => handleSkillNavigation('techtree')}
            >
              <div className="skill-icon">
                <i className="fas fa-server"></i>
              </div>
              <h3>Backend Development</h3>
              <p>Node.js, Python, Java, and server-side technologies</p>
              <div className="skill-tech-list">
                <span>Node.js</span>
                <span>Python</span>
                <span>Java</span>
                <span>Express.js</span>
              </div>
              <button className="skill-explore-btn">Explore Tech Tree</button>
            </motion.div>

            <motion.div 
              className="skill-category-card"
              whileHover={{ 
                scale: 1.03, 
                y: -8,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
              whileTap={{ scale: 0.97 }}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.06, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
              onClick={() => handleSkillNavigation('techtree')}
            >
              <div className="skill-icon">
                <i className="fas fa-database"></i>
              </div>
              <h3>Database Technologies</h3>
              <p>SQL, NoSQL, and modern database solutions</p>
              <div className="skill-tech-list">
                <span>MongoDB</span>
                <span>PostgreSQL</span>
                <span>Redis</span>
                <span>MySQL</span>
              </div>
              <button className="skill-explore-btn">Explore Tech Tree</button>
            </motion.div>


          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-glow" aria-hidden="true"></div>
        <div className="container">
          <div className="features-header">
            <h2>Everything You Need to Master Modern Development</h2>
          </div>
          
          <div className="features-grid">
            <motion.div 
              className="feature-card"
              whileHover={{ 
                scale: 1.03, 
                y: -6,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.02, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
            >
              <div className="feature-icon">
                <i className="fas fa-project-diagram"></i>
              </div>
              <h3>Interactive Tech Tree</h3>
              <p>Visual dependency mapping shows you exactly which technologies build on each other</p>
            </motion.div>

            <motion.div 
              className="feature-card"
              whileHover={{ 
                scale: 1.03, 
                y: -6,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.04, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
            >
              <div className="feature-icon">
                <i className="fas fa-route"></i>
              </div>
              <h3>Personalized Learning Paths</h3>
              <p>Get custom roadmaps based on your goals - frontend, backend, or fullstack</p>
            </motion.div>

            <motion.div 
              className="feature-card"
              whileHover={{ 
                scale: 1.03, 
                y: -6,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.06, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
            >
              <div className="feature-icon">
                <i className="fas fa-wand-magic-sparkles"></i>
              </div>
              <h3>Flip Card Learning</h3>
              <p>Engaging interactive cards with descriptions, use cases, and official resources</p>
            </motion.div>

            <motion.div 
              className="feature-card"
              whileHover={{ 
                scale: 1.03, 
                y: -6,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
            >
              <div className="feature-icon">
                <i className="fas fa-search"></i>
              </div>
              <h3>Smart Search & Filter</h3>
              <p>Find any technology instantly and filter by category, difficulty, or popularity</p>
            </motion.div>

            <motion.div 
              className="feature-card"
              whileHover={{ 
                scale: 1.03, 
                y: -6,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
            >
              <div className="feature-icon">
                <i className="fas fa-mobile-screen-button"></i>
              </div>
              <h3>Mobile Responsive</h3>
              <p>Learn anywhere, anytime with fully responsive design that works on all devices</p>
            </motion.div>

            <motion.div 
              className="feature-card"
              whileHover={{ 
                scale: 1.03, 
                y: -6,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.12, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
            >
              <div className="feature-icon">
                <i className="fas fa-rocket"></i>
              </div>
              <h3>Always Up-to-Date</h3>
              <p>Constantly updated with the latest technologies and industry best practices</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="social-proof-section">
        <div className="container">
          <motion.div 
            className="social-proof-content"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true }}
          >
            <h2>Trusted by Developers Worldwide</h2>
            
            <div className="testimonials-grid">
              <div className="testimonial">
                <div className="testimonial-content">
                  <p>"FullstackSchool saved me months of confusion. The tech tree visualization is genius!"</p>
                </div>
                <div className="testimonial-author">
                  <img src="https://ui-avatars.com/api/?name=Sarah+Chen&background=64ffda&color=0f172a&size=60" alt="Sarah Chen" />
                  <div>
                    <strong>Sarah Chen</strong>
                    <span>Frontend Developer at Meta</span>
                  </div>
                </div>
              </div>

              <div className="testimonial">
                <div className="testimonial-content">
                  <p>"Finally, a clear roadmap for learning backend technologies. No more tutorial hell!"</p>
                </div>
                <div className="testimonial-author">
                  <img src="https://ui-avatars.com/api/?name=Mike+Rodriguez&background=00ffff&color=0f172a&size=60" alt="Mike Rodriguez" />
                  <div>
                    <strong>Mike Rodriguez</strong>
                    <span>Full Stack Developer</span>
                  </div>
                </div>
              </div>

              <div className="testimonial">
                <div className="testimonial-content">
                  <p>"The interactive cards make learning new tech actually fun. Highly recommend!"</p>
                </div>
                <div className="testimonial-author">
                  <img src="https://ui-avatars.com/api/?name=Emily+Johnson&background=1db954&color=ffffff&size=60" alt="Emily Johnson" />
                  <div>
                    <strong>Emily Johnson</strong>
                    <span>DevOps Engineer</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="company-logos">
              <h4>Used by developers at</h4>
              <div className="logos-grid">
                <div className="company-logo" aria-hidden="true">
                  <svg width="80" height="28" viewBox="0 0 80 28" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="80" height="28" rx="4" fill="#E6EEF8" opacity="0.06"/><text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fill="#94a3b8" fontSize="10">Google</text></svg>
                </div>
                <div className="company-logo" aria-hidden="true">
                  <svg width="80" height="28" viewBox="0 0 80 28" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="80" height="28" rx="4" fill="#E6EEF8" opacity="0.06"/><text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fill="#94a3b8" fontSize="10">Microsoft</text></svg>
                </div>
                <div className="company-logo" aria-hidden="true">
                  <svg width="80" height="28" viewBox="0 0 80 28" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="80" height="28" rx="4" fill="#E6EEF8" opacity="0.06"/><text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fill="#94a3b8" fontSize="10">Amazon</text></svg>
                </div>
                <div className="company-logo" aria-hidden="true">
                  <svg width="80" height="28" viewBox="0 0 80 28" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="80" height="28" rx="4" fill="#E6EEF8" opacity="0.06"/><text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fill="#94a3b8" fontSize="10">Netflix</text></svg>
                </div>
                <div className="company-logo" aria-hidden="true">
                  <svg width="80" height="28" viewBox="0 0 80 28" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="80" height="28" rx="4" fill="#E6EEF8" opacity="0.06"/><text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fill="#94a3b8" fontSize="10">Spotify</text></svg>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true }}
          >
            <h2>Ready to Accelerate Your Development Journey?</h2>
            <p>Join 10,000+ developers who are already learning smarter, not harder.</p>
            
            <form className="email-signup" onSubmit={handleEmailSubmit}>
              <input 
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button type="submit" className="cta-primary">
                <i className="fas fa-paper-plane"></i>
                Get Early Access
              </button>
            </form>
            
            <div className="cta-features">
              <div className="cta-feature">
                <i className="fas fa-check"></i>
                <span>Free forever plan</span>
              </div>
              <div className="cta-feature">
                <i className="fas fa-check"></i>
                <span>No credit card required</span>
              </div>
              <div className="cta-feature">
                <i className="fas fa-check"></i>
                <span>Cancel anytime</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default LandingPage;